export class SetArray<T extends unknown[]> {
	constructor(initialItems?: T[]) {
		if (initialItems) {
			// Add each item using the add method to ensure uniqueness
			initialItems.forEach(item => this.add(item));
		}
	}

	private items: T[] = [];

	// Helper method to check if two arrays are equal
	#areArraysEqual(arr1: T, arr2: T): boolean {
		if (arr1.length !== arr2.length) return false;

		for (let i = 0; i < arr1.length; i++) {
			if (arr1[i] !== arr2[i]) return false;
		}

		return true;
	}

	// Add an array to the set if it doesn't exist
	add(item: T): this {
		if (!this.has(item)) {
			this.items.push(item);
		}
		return this;
	}

	// Check if an array exists in the set
	has(item: T): boolean {
		return this.items.some(existingItem => this.#areArraysEqual(existingItem, item));
	}

	// Delete an array from the set
	delete(item: T): boolean {
		const index = this.items.findIndex(existingItem => this.#areArraysEqual(existingItem, item));

		if (index !== -1) {
			this.items.splice(index, 1);
			return true;
		}

		return false;
	}

	// Get the size of the set
	get size(): number {
		return this.items.length;
	}

	// Clear all items from the set
	clear(): void {
		this.items = [];
	}

	// Convert set to array
	toArray(): T[] {
		return [...this.items];
	}

	// Custom toString method
	toString(): string {
		return JSON.stringify(this.items);
	}

	// Iterator implementation
	*[Symbol.iterator](): IterableIterator<T> {
		yield* this.items;
	}
}
